import { useState } from 'react';
import { Button } from '@/design-system';
import { ApiError } from '@/lib/api';
import { copySummary, downloadSummary, type SummaryFormat } from '@/lib/intake/summary';
import { COPY } from './copy';
import styles from './Journey.module.css';

/** "Keep a copy": the summary as PDF or text, or on the clipboard. On review and on confirmation. */
export function SummaryActions({ applicationId }: { applicationId: string }) {
  const [summary, setSummary] = useState({ busy: false, said: '', failed: false });

  async function keep(action: () => Promise<void>, done: string, failure: string) {
    setSummary({ busy: true, said: COPY.preparing, failed: false });
    try {
      await action();
      setSummary({ busy: false, said: done, failed: false });
    } catch (e) {
      // A refusal from the server says why; anything else is the clipboard's.
      setSummary({ busy: false, said: e instanceof ApiError ? COPY.summaryFailed : failure, failed: true });
    }
  }

  const download = (format: SummaryFormat) =>
    keep(() => downloadSummary(applicationId, format), '', COPY.summaryFailed);

  return (
    <section aria-labelledby="keep-copy" className={styles.reviewSection}>
      <h2 id="keep-copy" className={styles.subtitle}>
        {COPY.keepCopy}
      </h2>
      <p className={styles.intro}>{COPY.keepCopyIntro}</p>
      <div className={styles.inlineActions}>
        <Button variant="secondary" size="sm" disabled={summary.busy} onClick={() => void download('pdf')}>
          {COPY.downloadPdf}
        </Button>
        <Button variant="secondary" size="sm" disabled={summary.busy} onClick={() => void download('txt')}>
          {COPY.downloadText}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={summary.busy}
          onClick={() => void keep(() => copySummary(applicationId), COPY.copied, COPY.copyFailed)}
        >
          {COPY.copySummary}
        </Button>
      </div>
      <p role="status" className={summary.failed ? styles.summaryFailed : styles.summaryStatus}>
        {summary.said}
      </p>
    </section>
  );
}
